<template>
  <div class="footer-widgets">
    <div class="container">
      <div class="row" v-if="hasActiveWidgets">
        <div 
          v-for="(widget, index) in footerWidgets" 
          :key="index" 
          class="col-md-6 col-lg-3 mb-4"
        >
          <div v-if="widget.enabled && widget.type" class="footer-widget">
            <!-- Widget Title -->
            <h5 v-if="widget.title" class="widget-title">{{ widget.title }}</h5>
            
            <!-- Logo Widget -->
            <div v-if="widget.type === 'logo'" class="logo-widget">
              <div class="footer-logo-container">
                <img 
                  v-if="siteSettings.logo_url"
                  :src="`/backend/uploads/logos/${siteSettings.logo_url}`" 
                  :alt="siteSettings.company_name || 'Logo'"
                  class="logo-img"
                >
                <div v-else class="logo-fallback">
                  {{ siteSettings.company_name || 'LOGO' }}
                </div>
              </div>
            </div>
            
            <!-- Contact Widget -->
            <div v-if="widget.type === 'contact'" class="contact-widget">
              <div v-if="widget.show_phone && siteSettings.contact_phone" class="contact-item">
                <i class="bi bi-phone me-2"></i>
                <a :href="`tel:${siteSettings.contact_phone}`" class="contact-link">
                  {{ siteSettings.contact_phone }}
                </a>
              </div>
              <div v-if="widget.show_email && siteSettings.contact_email" class="contact-item">
                <i class="bi bi-envelope me-2"></i>
                <a :href="`mailto:${siteSettings.contact_email}`" class="contact-link">
                  {{ siteSettings.contact_email }}
                </a>
              </div>
              <div v-if="widget.show_address && siteSettings.contact_address" class="contact-item">
                <i class="bi bi-geo-alt me-2"></i>
                <span>{{ siteSettings.contact_address }}</span>
              </div>
              <div v-if="widget.show_working_hours && siteSettings.working_hours" class="contact-item">
                <i class="bi bi-clock me-2"></i>
                <span class="working-hours">{{ siteSettings.working_hours }}</span>
              </div>
            </div>
            
            <!-- Social Media Widget -->
            <div v-else-if="widget.type === 'social'" class="social-widget">
              <div class="social-links">
                <a 
                  v-if="widget.show_facebook && siteSettings.facebook_page_url" 
                  :href="siteSettings.facebook_page_url" 
                  class="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Facebook"
                >
                  <i class="bi bi-facebook"></i>
                </a>
                <a 
                  v-if="widget.show_instagram && siteSettings.instagram_url" 
                  :href="siteSettings.instagram_url" 
                  class="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Instagram"
                >
                  <i class="bi bi-instagram"></i>
                </a>
                <a 
                  v-if="widget.show_youtube && siteSettings.youtube_url" 
                  :href="siteSettings.youtube_url" 
                  class="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="YouTube"
                >
                  <i class="bi bi-youtube"></i>
                </a>
                <a 
                  v-if="widget.show_twitter && siteSettings.twitter_url" 
                  :href="siteSettings.twitter_url" 
                  class="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Twitter"
                >
                  <i class="bi bi-twitter"></i>
                </a>
              </div>
            </div>
            
            <!-- Google Maps Widget -->
            <div v-else-if="widget.type === 'google_maps' && siteSettings.google_maps_embed" class="google-maps-widget">
              <div class="map-container">
                <iframe 
                  :src="siteSettings.google_maps_embed"
                  width="100%" 
                  height="250" 
                  style="border:0; border-radius: 8px;" 
                  allowfullscreen
                  loading="lazy" 
                  referrerpolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
            
            <!-- Facebook Embed Widget -->
            <div v-else-if="widget.type === 'facebook_embed' && siteSettings.facebook_page_url" class="facebook-embed-widget">
              <div class="facebook-container">
                <div id="fb-root"></div>
                <div class="fb-page" 
                     :data-href="siteSettings.facebook_page_url"
                     data-tabs="timeline" 
                     data-width="300" 
                     data-height="350" 
                     data-small-header="false" 
                     data-adapt-container-width="true" 
                     data-hide-cover="false" 
                     data-show-facepile="true">
                  <blockquote :cite="siteSettings.facebook_page_url" class="fb-xfbml-parse-ignore">
                    <a :href="siteSettings.facebook_page_url" target="_blank">
                      {{ siteSettings.company_name || 'Facebook Page' }}
                    </a>
                  </blockquote>
                </div>
              </div>
            </div>
            
            <!-- Text Widget -->
            <div v-else-if="widget.type === 'text' && widget.content" class="text-widget">
              <p>{{ widget.content }}</p>
            </div>
            
            <!-- Links Widget -->
            <div v-else-if="widget.type === 'links' && widget.links && widget.links.length > 0" class="links-widget">
              <ul class="footer-links-list">
                <li v-for="link in widget.links" :key="link.text" class="footer-link-item">
                  <a 
                    v-if="link.text && link.url" 
                    :href="link.url" 
                    class="footer-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ link.text }}
                  </a>
                </li>
              </ul>
            </div>
            
            <!-- Recent Posts Widget -->
            <div v-else-if="widget.type === 'recent_posts'" class="recent-posts-widget">
              <div v-if="recentPosts.length > 0" class="posts-list">
                <div 
                  v-for="post in recentPosts.slice(0, widget.count || 5)" 
                  :key="post.id" 
                  class="post-item"
                >
                  <router-link :to="`/post/${post.slug}`" class="post-link">
                    <h6 class="post-title">{{ post.title }}</h6>
                    <small class="post-date">
                      <i class="bi bi-calendar3 me-1"></i>
                      {{ formatDate(post.created_at) }}
                    </small>
                  </router-link>
                </div>
              </div>
              <p v-else class="no-posts">Δεν υπάρχουν πρόσφατα άρθρα</p>
            </div>
            
            <!-- Categories Widget -->
            <div v-else-if="widget.type === 'categories'" class="categories-widget">
              <div v-if="postCategories.length > 0" class="categories-list">
                <div v-for="category in postCategories" :key="category.id" class="category-item">
                  <router-link :to="`/category/${category.slug}`" class="category-link">
                    <i class="bi bi-folder me-2"></i>
                    {{ category.name }}
                    <span v-if="category.post_count" class="post-count">({{ category.post_count }})</span>
                  </router-link>
                </div>
              </div>
              <p v-else class="no-categories">Δεν υπάρχουν κατηγορίες</p>
            </div>
            
            <!-- Custom HTML Widget -->
            <div v-else-if="widget.type === 'custom_html' && widget.html" class="custom-html-widget">
              <div v-html="widget.html"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { settingsService } from '@/services/api'

// Props
interface FooterWidgetLink {
  text: string
  url: string
}

interface FooterWidget {
  type: string
  title: string
  content: string
  links: FooterWidgetLink[]
  html: string
  count: number
  enabled: boolean
  // Contact widget settings
  show_phone?: boolean
  show_email?: boolean
  show_address?: boolean
  show_working_hours?: boolean
  // Social widget settings
  show_facebook?: boolean
  show_instagram?: boolean
  show_youtube?: boolean
  show_twitter?: boolean
}

// State
const footerWidgets = ref<FooterWidget[]>([])
const siteSettings = ref<any>({})
const recentPosts = ref<any[]>([])
const postCategories = ref<any[]>([])

// Computed
const hasActiveWidgets = computed(() => {
  return footerWidgets.value.some(widget => widget.enabled && widget.type)
})

// Load footer widgets from settings
const loadFooterWidgets = async () => {
  try {
    const response = await settingsService.getSettings()
    if (response.success && response.settings) {
      // Store all site settings with proper boolean conversion for show/hide flags
      siteSettings.value = {
        ...response.settings,
        // Convert show/hide settings to proper booleans
        show_phone: response.settings.show_phone !== undefined ? 
          (response.settings.show_phone === true || response.settings.show_phone === 'true' || response.settings.show_phone === '1') : true,
        show_email: response.settings.show_email !== undefined ? 
          (response.settings.show_email === true || response.settings.show_email === 'true' || response.settings.show_email === '1') : true,
        show_address: response.settings.show_address !== undefined ? 
          (response.settings.show_address === true || response.settings.show_address === 'true' || response.settings.show_address === '1') : true,
        show_working_hours: response.settings.show_working_hours !== undefined ? 
          (response.settings.show_working_hours === true || response.settings.show_working_hours === 'true' || response.settings.show_working_hours === '1') : true,
        show_instagram: response.settings.show_instagram !== undefined ? 
          (response.settings.show_instagram === true || response.settings.show_instagram === 'true' || response.settings.show_instagram === '1') : true,
        show_youtube: response.settings.show_youtube !== undefined ? 
          (response.settings.show_youtube === true || response.settings.show_youtube === 'true' || response.settings.show_youtube === '1') : true,
        show_twitter: response.settings.show_twitter !== undefined ? 
          (response.settings.show_twitter === true || response.settings.show_twitter === 'true' || response.settings.show_twitter === '1') : true
      }
      
      
      // Parse footer widgets
      if (response.settings.footer_widgets) {
        try {
          const parsedWidgets = JSON.parse(response.settings.footer_widgets)
          if (Array.isArray(parsedWidgets)) {
            footerWidgets.value = parsedWidgets
          }
        } catch (error) {
        }
      }
    }
  } catch (error) {
  }
}

// Load recent posts for recent posts widget
const loadRecentPosts = async () => {
  try {
    // Mock data for now - will implement later when posts service is available
    recentPosts.value = [
      { id: 1, title: 'Πρόσφατο Άρθρο 1', slug: 'article-1', created_at: new Date().toISOString() },
      { id: 2, title: 'Πρόσφατο Άρθρο 2', slug: 'article-2', created_at: new Date().toISOString() },
    ]
  } catch (error) {
  }
}

// Load categories for categories widget
const loadCategories = async () => {
  try {
    // Mock data for now - will implement later when posts service is available
    postCategories.value = [
      { id: 1, name: 'Κατηγορία 1', slug: 'category-1', post_count: 5 },
      { id: 2, name: 'Κατηγορία 2', slug: 'category-2', post_count: 3 },
    ]
  } catch (error) {
  }
}

// Format date helper
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('el-GR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (error) {
    return dateString
  }
}

// Load Facebook SDK only if Facebook page URL is set
const loadFacebookSDK = () => {
  // Check if SDK is already loaded
  if (document.getElementById('facebook-jssdk')) {
    return
  }
  
  // Only load if Facebook page URL exists
  if (!siteSettings.value.facebook_page_url) {
    return
  }
  
  // Load Facebook SDK script
  const script = document.createElement('script')
  script.id = 'facebook-jssdk'
  script.src = 'https://connect.facebook.net/el_GR/sdk.js#xfbml=1&version=v18.0'
  script.async = true
  script.defer = true
  script.crossOrigin = 'anonymous'
  
  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  }
}

// Load all data on mount
onMounted(() => {
  loadFooterWidgets().then(() => {
    // Load Facebook SDK after settings are loaded
    loadFacebookSDK()
  })
  loadRecentPosts()
  loadCategories()
})
</script>

<style scoped>
.footer-widgets {
  padding: 2rem 0;
}

.footer-widget {
  height: 100%;
  text-align: center; /* Center align all footer widgets by default */
}

.widget-title {
  color: inherit;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(255,255,255,0.3);
  display: inline-block;
}

/* Contact Widget */
.contact-widget .contact-item {
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center; /* Center contact items */
  font-size: 0.95rem;
  line-height: 1.4;
  gap: 0.5rem;
}

.contact-widget .contact-item i {
  margin-top: 0.1rem;
  opacity: 0.8;
}

/* Preserve line breaks for working hours */
.contact-widget .working-hours {
  white-space: pre-line;
}

.contact-link {
  color: inherit;
  text-decoration: none;
  transition: opacity 0.3s ease;
}

.contact-link:hover {
  opacity: 0.8;
  color: inherit;
}

/* Text Widget */
.text-widget p {
  margin: 0;
  line-height: 1.6;
  color: inherit;
  opacity: 0.9;
}

/* Links Widget */
.footer-links-list {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: center; /* Center links list */
}

.footer-link-item {
  margin-bottom: 0.5rem;
}

.footer-link {
  color: inherit;
  text-decoration: none;
  font-size: 0.95rem;
  transition: opacity 0.3s ease;
  display: inline-block;
}

.footer-link:hover {
  opacity: 0.8;
  color: inherit;
  text-decoration: underline;
}

/* Social Widget */
.social-links {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center; /* Center social icons */
}

.social-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255,255,255,0.1);
  color: inherit;
  text-decoration: none;
  font-size: 1.2rem;
  transition: background-color 0.3s ease, transform 0.2s ease;
}

.social-link:hover {
  background: rgba(255,255,255,0.2);
  color: inherit;
  transform: translateY(-2px);
}

/* Google Maps Widget */
.google-maps-widget .map-container {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

/* Logo Widget */
.logo-widget {
  text-align: center;
}

.footer-logo-container {
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.logo-img {
  max-width: 200px;
  max-height: 100px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  display: block;
  margin: 0 auto;
}

.logo-fallback {
  font-size: 1.5rem;
  font-weight: 700;
  color: inherit;
  opacity: 0.95;
}

/* Facebook Embed Widget */
.facebook-embed-widget {
  margin-top: 0.5rem;
}

.facebook-container {
  background: rgba(255,255,255,0.05);
  padding: 0.75rem;
  border-radius: 8px;
  overflow: hidden;
}

.facebook-container .fb-page {
  display: block;
  width: 100%;
}

.facebook-container iframe {
  border-radius: 4px;
}

/* Recent Posts Widget */
.post-item {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.post-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.post-link {
  color: inherit;
  text-decoration: none;
  display: block;
}

.post-title {
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
  line-height: 1.3;
  transition: opacity 0.3s ease;
}

.post-link:hover .post-title {
  opacity: 0.8;
}

.post-date {
  color: inherit;
  opacity: 0.7;
  font-size: 0.8rem;
}

/* Categories Widget */
.category-item {
  margin-bottom: 0.5rem;
}

.category-link {
  color: inherit;
  text-decoration: none;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  transition: opacity 0.3s ease;
}

.category-link:hover {
  opacity: 0.8;
  color: inherit;
}

.post-count {
  margin-left: 0.5rem;
  opacity: 0.7;
  font-size: 0.85rem;
}

/* Custom HTML Widget */
.custom-html-widget {
  color: inherit;
}

.custom-html-widget :deep(h1),
.custom-html-widget :deep(h2),
.custom-html-widget :deep(h3),
.custom-html-widget :deep(h4),
.custom-html-widget :deep(h5),
.custom-html-widget :deep(h6) {
  color: inherit;
}

.custom-html-widget :deep(a) {
  color: inherit;
  opacity: 0.9;
}

.custom-html-widget :deep(a:hover) {
  opacity: 0.7;
}

/* Empty states */
.no-posts,
.no-categories {
  margin: 0;
  opacity: 0.7;
  font-style: italic;
  color: inherit;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .footer-widgets {
    padding: 1.5rem 0;
  }
  
  .footer-widget {
    margin-bottom: 2rem;
    text-align: center; /* Κεντράρισμα στο mobile */
  }
  
  .footer-widget:last-child {
    margin-bottom: 0;
  }
  
  .widget-title {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    text-align: center; /* Κεντράρισμα τίτλου */
  }
  
  /* Contact Widget Mobile */
  .contact-item {
    font-size: 0.9rem;
    margin-bottom: 0.8rem;
    justify-content: center; /* Κεντράρισμα contact items */
    align-items: center;
    text-align: center;
    flex-direction: column; /* Stack icon above text on mobile */
  }
  
  .contact-item i {
    font-size: 1rem;
    margin-right: 0;
    margin-bottom: 0.3rem;
  }
  
  /* Social Links Mobile */
  .social-links {
    justify-content: center;
    gap: 1rem;
  }
  
  .social-link {
    width: 45px;
    height: 45px;
    font-size: 1.3rem;
  }
  
  /* Logo Widget Mobile */
  .logo-widget {
    text-align: center;
  }
  
  .footer-logo-container {
    justify-content: center;
  }
  
  .logo-img {
    max-width: 160px;
    max-height: 80px;
  }
  
  .logo-fallback {
    font-size: 1.3rem;
    margin: 0 auto;
  }
  
  /* Google Maps Mobile */
  .google-maps-widget .map-container iframe {
    height: 200px;
  }
  
  /* Facebook Embed Mobile */
  .facebook-embed-widget {
    text-align: center;
  }
  
  .facebook-container {
    padding: 0.5rem;
    display: flex;
    justify-content: center;
  }
  
  .facebook-container .fb-page {
    max-width: 100%;
  }
  
  /* Text Widget Mobile */
  .text-widget {
    text-align: center;
  }
  
  .text-content {
    font-size: 0.9rem;
  }
  
  /* Links Widget Mobile */
  .links-widget {
    text-align: center;
  }
  
  .footer-links-list {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .footer-link {
    font-size: 0.9rem;
  }
  
  /* Recent Posts Mobile */
  .recent-posts-widget {
    text-align: center;
  }
  
  .post-title {
    font-size: 0.9rem;
  }
  
  .post-date {
    font-size: 0.75rem;
  }
}

/* Small Mobile Devices */
@media (max-width: 480px) {
  .footer-widgets {
    padding: 1rem 0;
  }
  
  .footer-widget {
    margin-bottom: 1.5rem;
    text-align: center; /* Κεντράρισμα στο small mobile */
  }
  
  .widget-title {
    font-size: 1rem;
    margin-bottom: 0.8rem;
    padding-bottom: 0.4rem;
    text-align: center;
  }
  
  /* Contact Widget Small Mobile */
  .contact-item {
    font-size: 0.85rem;
    margin-bottom: 0.7rem;
    flex-direction: column;
    align-items: center; /* Κεντράρισμα */
    gap: 0.3rem;
    text-align: center;
  }
  
  .contact-item i {
    font-size: 0.9rem;
    margin-right: 0;
    margin-bottom: 0.2rem;
  }
  
  .contact-details {
    margin-left: 0;
    text-align: center;
  }
  
  /* Social Links Small Mobile */
  .social-links {
    gap: 0.8rem;
    justify-content: center;
  }
  
  .social-link {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
  
  /* Logo Widget Small Mobile */
  .logo-widget {
    text-align: center;
  }
  
  .footer-logo-container {
    justify-content: center;
  }
  
  .logo-img {
    max-width: 140px;
    max-height: 70px;
  }
  
  .logo-fallback {
    font-size: 1.1rem;
    margin: 0 auto;
  }
  
  /* Google Maps Small Mobile */
  .google-maps-widget .map-container iframe {
    height: 180px;
    border-radius: 6px;
  }
  
  /* Facebook Embed Small Mobile */
  .facebook-embed-widget {
    text-align: center;
  }
  
  .facebook-container {
    padding: 0.4rem;
    display: flex;
    justify-content: center;
  }
  
  /* Text Widget Small Mobile */
  .text-widget {
    text-align: center;
  }
  
  .text-content {
    font-size: 0.85rem;
    line-height: 1.5;
  }
  
  /* Links Widget Small Mobile */
  .links-widget {
    text-align: center;
  }
  
  .footer-links-list {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .footer-link-item {
    margin-bottom: 0.4rem;
  }
  
  .footer-link {
    font-size: 0.85rem;
  }
  
  /* Recent Posts Small Mobile */
  .recent-posts-widget {
    text-align: center;
  }
  
  .post-item {
    margin-bottom: 0.8rem;
    padding-bottom: 0.6rem;
  }
  
  .post-title {
    font-size: 0.85rem;
  }
  
  .post-date {
    font-size: 0.7rem;
  }
  
  /* Categories Small Mobile */
  .category-link {
    font-size: 0.85rem;
  }
  
  .post-count {
    font-size: 0.75rem;
  }
}

/* Tablet Portrait */
@media (min-width: 769px) and (max-width: 991px) {
  .footer-widgets {
    padding: 2rem 0;
  }
  
  .widget-title {
    font-size: 1.15rem;
  }
  
  .social-links {
    justify-content: flex-start;
  }
  
  .logo-img {
    max-width: 180px;
    max-height: 90px;
  }
  
  .google-maps-widget .map-container iframe {
    height: 220px;
  }
}
</style>